import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, Modal, Button, Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import EditModal from "./EditModal";
import { FaEdit, FaLock, FaUniversity } from "react-icons/fa";
import { BsCamera } from "react-icons/bs";
import { User, Users } from "../../types/Types";
import { useAppContext } from "../../Context/AppContext";
import { useGetUser } from "../../api/queries/useGetUser";
import { useGetBilling } from "../../api/queries/useGetBilling";
import { useDeletePaymentMethod } from "../../api/mutations/useDeletePaymentMethod";
import { getAccessTokenObjectId } from "../../auth/authStorage";
import { IoLocationOutline } from "react-icons/io5";
import BusinessProfile, {
  BusinessContactDetails,
  BusinessMembershipTags,
} from "./BusinessProfile";
import YouthProfile, {
  YouthContactDetails,
  YouthMembershipTags,
} from "./YouthProfile";
import UploadImage from "./UploadImage";
import CurvedTextOverlay from "./CurvedTextOverlay";
import BusinessLogoPreview from "./BusinessLogoPreview";
import { useMediaQuery } from "react-responsive";
import AddPaymentMethodModal from "./AddPaymentMethodModal";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { resolveProfessionalPhotoUrl } from "../../utils/profilePhoto";
import TranslatableText from "../common/TranslatableText";
import {
  isEarlyStageMembership,
  isLegacyYouthMembership,
  isPaidPortalMember,
} from "../../utils/membershipTier";
import { isUnpaidExplorerUser } from "../../explorer/isUnpaidExplorerUser";
import "../css/ProfileModal.css";
import "../css/MyAccount.css";

const MyAccount: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showProfilePhotoModal, setShowProfilePhotoModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [removingMethodId, setRemovingMethodId] = useState<string | null>(null);
  const { objectId, setObjectId } = useAppContext();
  const tokenObjectId = useMemo(() => getAccessTokenObjectId(), []);
  const effectiveObjectId = tokenObjectId || objectId || "";

  useEffect(() => {
    if (tokenObjectId && tokenObjectId !== objectId) {
      setObjectId(tokenObjectId);
    }
  }, [objectId, setObjectId, tokenObjectId]);

  const { data: userResponse } = useGetUser({
    id: effectiveObjectId,
    enabled: !!effectiveObjectId,
    refetchOnMount: true,
  });
  const { data: paymentMethods, refetch: refetchPaymentMethods } =
    useGetBilling({ userId: effectiveObjectId });
  const userData = { ...userResponse?.user, ...userResponse?.imageUrls };
  const profilePhotoSrc = resolveProfessionalPhotoUrl(userData, "/user.png");
  const isExplorer = useMemo(
    () => isUnpaidExplorerUser(userResponse?.user),
    [userResponse?.user],
  );
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const imageSize = isDesktop ? 150 : isLgTablet ? 140 : isMdTablet ? 130 : 95;

  const { mutate: deletePaymentMethod } = useDeletePaymentMethod({
    onSuccess: () => {
      setRemovingMethodId(null);
      setShowRemoveModal(false);
      refetchPaymentMethods();
    },
  });

  const handleEdit = () => {
    if (isExplorer || !userData) return;
    setShowEditModal(true);
  };

  const handleBannerEdit = () => {
    if (isExplorer) return;
    setShowBannerModal(true);
  };

  const handleProfilePhotoEdit = () => {
    setShowProfilePhotoModal(true);
  };

  const handleConfirmRemove = () => {
    if (!selectedPaymentMethod?.id) return;
    setRemovingMethodId(selectedPaymentMethod.id);
    deletePaymentMethod(selectedPaymentMethod.id);
  };

  const fields = Object.entries(userData || {}).filter(
    ([key, value]) =>
      key.trim() !== "" &&
      value !== null &&
      value !== "" &&
      ![
        "password",
        "objectId",
        "isGDPRConsent",
        "isEmailUpdates",
        "isTermConditions",
        "isDirectory",
        "hasPaid",
        "hasCompletedProfile",
        "id",
        "membershipId",
        "consentToShareProfile",
        "isAdmin",
        "membershipType",
        "discountEndDate",
        "discountCode",
        "membershipName",
        "isAnnualMembership",
        "BusinessLogo",
        "PersonalProfessionalPhoto",
        "businessAddressLineTwo",
        "county",
        "businessAbout",
        "personalAbout",
        "businessProductServices",
        "businessDiscountCode",
        "businessDiscount",
        "ProfessionalPhoto",
        "skillsExperience",
      ].includes(key)
  );

  const totalPossibleFields = 18;
  const filledFieldsCount = fields.length;
  const completionPercent = Math.min(
    Math.round((filledFieldsCount / totalPossibleFields) * 100),
    100
  );

  const accountFullName = useMemo(() => {
    const first =
      (userData.firstName || "").charAt(0).toUpperCase() +
      (userData.firstName || "").slice(1);
    const last =
      (userData.lastName || "").charAt(0).toUpperCase() +
      (userData.lastName || "").slice(1);
    return `${first} ${last}`.trim();
  }, [userData.firstName, userData.lastName]);

  const headerFields = useMemo(
    () => ({
      fullName: accountFullName,
      studyInterest: userData.studyInterest,
      personalPositionCompany: userData.personalPositionCompany,
      city: userData.city,
      country: userData.country,
      universityCollege: userData.universityCollege,
      businessName: userData.businessName,
      membershipName: userData.membershipName,
    }),
    [
      accountFullName,
      userData.businessName,
      userData.city,
      userData.country,
      userData.membershipName,
      userData.personalPositionCompany,
      userData.studyInterest,
      userData.universityCollege,
    ]
  );
  const { data: translatedHeader } = useTranslateDynamicFields(
    headerFields,
    i18n.resolvedLanguage || i18n.language
  );
  const tx = (key: keyof typeof headerFields, fallback?: string) =>
    translatedHeader?.[key] || fallback || "";

  const isEarlyStageMember = isEarlyStageMembership(userData?.membershipName);
  const isLegacyYouthMember = isLegacyYouthMembership(userData?.membershipName);
  const isYouthLike = isLegacyYouthMember || isEarlyStageMember;
  const showMembershipBadge = isPaidPortalMember(userData);
  const bannerActionLabel = userData.BannerImage
    ? t("account.changeBanner")
    : t("account.addBanner");

  const billingCycleLabel = userData.isAnnualMembership
    ? t("account.billingCycle.annual")
    : t("account.billingCycle.monthly");

  const membershipNameDisplay =
    tx("membershipName", userData.membershipName) ||
    userData.membershipName ||
    "";
  const membershipSummary = membershipNameDisplay
    ? `${membershipNameDisplay} · ${billingCycleLabel}`
    : t("account.membershipBillingEmpty");

  const uniquePaymentMethods = useMemo(() => {
    if (!paymentMethods?.length) return [];
    return paymentMethods
      .filter((method: any) => method?.last4)
      .filter(
        (method: any, index: number, self: any[]) =>
          index ===
          self.findIndex(
            (m) => m.brand === method.brand && m.last4 === method.last4
          )
      );
  }, [paymentMethods]);

  const paymentSubtitle =
    uniquePaymentMethods.length > 0
      ? t("account.cardsOnFile", { count: uniquePaymentMethods.length })
      : t("account.noSavedPaymentMethods");

  const accountActionButtons = (fullWidth = false) => {
    const editButton = (
      <Button
        variant={isExplorer ? "outline-secondary" : "outline-primary"}
        size="sm"
        className={`shadow-sm ${
          isExplorer ? "" : "btn-hover-project-gradient"
        } ${fullWidth ? "w-100" : ""}`}
        onClick={handleEdit}
        disabled={isExplorer}
        aria-label={
          isExplorer
            ? t("account.editProfileRequiresMembership")
            : t("account.editProfile")
        }
      >
        {isExplorer ? (
          <FaLock size={13} className="me-1" aria-hidden />
        ) : (
          <FaEdit size={14} className="me-1" aria-hidden />
        )}
        {t("account.editProfile")}
      </Button>
    );

    return (
      <div
        className={`d-flex gap-2 ${
          fullWidth ? "w-100" : "flex-wrap justify-content-end"
        }`}
      >
        {isExplorer ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="my-account-edit-profile-locked">
                {t("account.editProfileRequiresMembership")}
              </Tooltip>
            }
          >
            <span
              className={fullWidth ? "flex-fill d-inline-block" : "d-inline-block"}
            >
              {editButton}
            </span>
          </OverlayTrigger>
        ) : (
          editButton
        )}
      </div>
    );
  };

  return (
    <div className="my-account-page mt-0 pb-4">
      <div className="card border-0 shadow-sm pb-2 overflow-hidden">
        <div>
          <div
            className={`position-relative ${
              userData.BannerImage ? "" : "bg-project-gradient"
            } profile-banner p-2`}
            style={{
              backgroundImage: `url(${userData.BannerImage || ""})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "100%",
              height: "100%",
              aspectRatio: "3 / 1",
            }}
          >
            {!userData.BannerImage && (
              <div className="d-flex h-100 justify-content-center align-items-center">
                <img
                  src="/SMBF-logo-white.png"
                  alt={t("account.alt.brandLogo")}
                  className="w-25 h-25"
                />
              </div>
            )}
            {isExplorer ? (
              <OverlayTrigger
                placement="left"
                overlay={
                  <Tooltip id="my-account-banner-locked">
                    {t("account.editProfileRequiresMembership")}
                  </Tooltip>
                }
              >
                <span
                  className="position-absolute bottom-0 end-0 m-2 d-inline-flex"
                  style={{ zIndex: 2 }}
                >
                  <button
                    type="button"
                    className="btn btn-light btn-sm d-inline-flex align-items-center justify-content-center rounded-circle shadow border-0"
                    style={{ width: 36, height: 36 }}
                    disabled
                    aria-label={t("account.editProfileRequiresMembership")}
                  >
                    <FaLock size={14} aria-hidden />
                  </button>
                </span>
              </OverlayTrigger>
            ) : (
              <button
                type="button"
                className="btn btn-light btn-sm position-absolute bottom-0 end-0 m-2 d-inline-flex align-items-center justify-content-center rounded-circle shadow border-0"
                style={{ width: 36, height: 36, zIndex: 2 }}
                onClick={handleBannerEdit}
                aria-label={bannerActionLabel}
                title={bannerActionLabel}
              >
                <BsCamera size={16} aria-hidden />
              </button>
            )}
            <div className="position-absolute translate-middle image-container">
              <div
                className="position-relative w-100 h-100 my-account-avatar"
                onClick={handleProfilePhotoEdit}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleProfilePhotoEdit();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={t("account.updateProfilePhoto")}
              >
                <Image
                  src={profilePhotoSrc}
                  roundedCircle
                  alt={t("account.alt.profilePhoto")}
                  className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                />
                <button
                  type="button"
                  className="my-account-avatar__update"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProfilePhotoEdit();
                  }}
                  aria-label={t("account.updateProfilePhoto")}
                  title={t("account.updateProfilePhoto")}
                >
                  <BsCamera size={18} aria-hidden />
                  <span className="my-account-avatar__update-label">
                    {t("account.updateProfilePhoto")}
                  </span>
                </button>

                <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                  {showMembershipBadge && (
                    <CurvedTextOverlay
                      size={imageSize}
                      stroke={
                        isEarlyStageMember
                          ? "#18c2c6"
                          : isLegacyYouthMember
                            ? "#18c2c6"
                            : userData?.membershipName === "Business Membership"
                              ? "#1c57b9"
                              : "#544bc1"
                      }
                      text={
                        isEarlyStageMember
                          ? "#EarlyStage"
                          : isLegacyYouthMember
                            ? "#Youth"
                            : userData?.membershipName === "Business Membership"
                              ? "#Business"
                              : "#Executive"
                      }
                    />
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="mx-3 pt-5 flex-grow-1">
            <div
              className="d-flex justify-content-between align-items-start gap-3"
              style={{ direction: "ltr" }}
            >
              <div className="mt-md-4 mt-lg-5 text-start min-w-0 flex-grow-1">
                <h2 className="mb-0">
                  <TranslatableText text={tx("fullName", accountFullName)} />
                </h2>
                <span>
                  {isYouthLike
                    ? userData.studyInterest && (
                        <TranslatableText
                          text={tx(
                            "studyInterest",
                            userData.studyInterest.charAt(0).toUpperCase() +
                              userData.studyInterest.slice(1)
                          )}
                        />
                      )
                    : userData.personalPositionCompany && (
                        <TranslatableText
                          text={tx(
                            "personalPositionCompany",
                            userData.personalPositionCompany.charAt(0).toUpperCase() +
                              userData.personalPositionCompany.slice(1)
                          )}
                        />
                      )}
                </span>
                {(userData.city || userData.country) && (
                  <div className="d-flex gap-1">
                    <IoLocationOutline className="mt-1 flex-shrink-0" />
                    <span className="text-muted text-truncate">
                      <TranslatableText text={tx("city", userData.city)} />,{" "}
                      <TranslatableText text={tx("country", userData.country)} />
                    </span>
                  </div>
                )}
              </div>
              <div className="d-none d-md-flex flex-column align-items-end gap-2 flex-shrink-0">
                {isYouthLike ? (
                  <div className="fw-semibold d-flex gap-1 align-items-center">
                    <FaUniversity size={25} className="flex-shrink-0" />
                    <span className="text-truncate" style={{ maxWidth: 180 }}>
                      <TranslatableText
                        text={tx(
                          "universityCollege",
                          userData.universityCollege
                        )}
                      />
                    </span>
                  </div>
                ) : (
                  <div className="d-flex gap-2 align-items-center">
                    <BusinessLogoPreview
                      src={userData.BusinessLogo}
                      name={userData.businessName}
                      size={40}
                      alt={t("account.alt.brandLogo")}
                    />
                    <div
                      className="fw-semibold text-truncate"
                      style={{ maxWidth: 160 }}
                    >
                      <TranslatableText
                        text={tx("businessName", userData.businessName)}
                      />
                    </div>
                  </div>
                )}
                {accountActionButtons()}
                <button
                  type="button"
                  className="my-account-view-contact"
                  onClick={() => setShowContactModal(true)}
                >
                  {t("account.viewPersonalContact")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {completionPercent < 100 && (
        <div className="progress mt-3" style={{ height: "15px" }}>
          <div
            className="progress-bar bg-success"
            role="progressbar"
            style={{ width: `${completionPercent}%` }}
            aria-valuenow={completionPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span className="text-dark text-md-white">
              {t("account.profileCompleted", { percent: completionPercent })}
            </span>
          </div>
        </div>
      )}

      <div className="d-md-none mt-3 px-0 text-center">
        {accountActionButtons(true)}
        <button
          type="button"
          className="my-account-view-contact mt-2"
          onClick={() => setShowContactModal(true)}
        >
          {t("account.viewPersonalContact")}
        </button>
      </div>

      {userData.id &&
        (isYouthLike ? (
          <YouthProfile userData={userData as User} />
        ) : (
          <BusinessProfile userData={userData as User} />
        ))}

      <Accordion className="my-account-accordion" alwaysOpen={false}>
        <Accordion.Item eventKey="membership">
          <Accordion.Header>
            <span className="d-block text-start pe-2">
              {t("account.membershipAndBilling")}
              <span className="my-account-accordion__subtitle">
                {membershipSummary}
              </span>
            </span>
          </Accordion.Header>
          <Accordion.Body>
            {userData.id ? (
              isYouthLike ? (
                <YouthMembershipTags userData={userData as User} />
              ) : (
                <BusinessMembershipTags userData={userData as User} />
              )
            ) : null}
            <p className="text-muted small mt-3 mb-2">
              {t("account.upgradeOrCancelMembership")}
            </p>
            <Button
              variant="outline-primary"
              className="w-100 shadow-sm btn-hover-project-gradient"
              onClick={() => navigate("/manage-membership")}
            >
              {t("membership.manageTitle")}
            </Button>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="payments">
          <Accordion.Header>
            <span className="d-block text-start pe-2">
              {t("account.paymentMethods")}
              <span className="my-account-accordion__subtitle">
                {paymentSubtitle}
              </span>
            </span>
          </Accordion.Header>
          <Accordion.Body>
            {uniquePaymentMethods.length > 0 ? (
              <>
                {uniquePaymentMethods.map((method: any, index: number) => (
                  <div
                    key={method.id ?? index}
                    className="d-flex justify-content-between align-items-center border rounded-3 p-3 mb-2"
                  >
                    <p className="mb-0 fw-semibold">
                      {method.brand?.toUpperCase()} •••• {method.last4}
                    </p>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setSelectedPaymentMethod(method);
                        setShowRemoveModal(true);
                      }}
                    >
                      {t("connections.remove")}
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2 shadow-sm btn-hover-project-gradient"
                  onClick={() => setShowAddCardModal(true)}
                >
                  {t("account.addPaymentMethod")}
                </Button>
              </>
            ) : (
              <>
                <p className="text-muted small mb-2">
                  {t("account.noSavedPaymentMethods")}
                </p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="shadow-sm btn-hover-project-gradient"
                  onClick={() => setShowAddCardModal(true)}
                >
                  {t("account.addPaymentMethod")}
                </Button>
              </>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <AddPaymentMethodModal
        show={showAddCardModal}
        onHide={() => setShowAddCardModal(false)}
        userId={effectiveObjectId}
        onSuccess={() => refetchPaymentMethods()}
      />

      <Modal
        centered
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("account.personalContactTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userData.id ? (
            isYouthLike ? (
              <YouthContactDetails userData={userData as User} />
            ) : (
              <BusinessContactDetails userData={userData as User} />
            )
          ) : null}
          <p className="my-account-privacy-note mb-0 d-flex align-items-start gap-2">
            <FaLock className="flex-shrink-0 mt-1" size={12} aria-hidden />
            <span>{t("account.personalContactPrivacy")}</span>
          </p>
        </Modal.Body>
      </Modal>

      <EditModal
        showEditModal={showEditModal && !isExplorer}
        setShowEditModal={() => setShowEditModal(false)}
        userData={userData && "id" in userData ? (userResponse as Users) : null}
        completionPercent={completionPercent}
      />
      <Modal
        centered
        show={showRemoveModal}
        onHide={() => setShowRemoveModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("account.removePaymentMethod")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("account.removePaymentMethodBody", {
            brand: selectedPaymentMethod?.brand?.toUpperCase() ?? "",
            last4: selectedPaymentMethod?.last4 ?? "",
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
            {t("connections.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmRemove}
            disabled={removingMethodId === selectedPaymentMethod?.id}
          >
            {removingMethodId === selectedPaymentMethod?.id
              ? t("feed.removing")
              : t("connections.remove")}
          </Button>
        </Modal.Footer>
      </Modal>
      <UploadImage
        showBannerModal={showBannerModal && !isExplorer}
        setShowBannerModal={setShowBannerModal}
        userData={userData as User}
        variant="banner"
      />
      <UploadImage
        showBannerModal={showProfilePhotoModal}
        setShowBannerModal={setShowProfilePhotoModal}
        userData={userData as User}
        variant="profile"
      />
    </div>
  );
};

export default MyAccount;
